// Display helpers shared by the patient list and report sheet.

// "Sep 26, 2026, 7:05 AM", or '' for a missing or invalid time.
export function formatDateTime(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// "Day shift · Oct 5" for { date: '2026-10-05', shift: 'day' }. A night shift
// is labeled with the date it starts. The date is a calendar date, so it's
// read as-is rather than as a UTC instant.
export function formatShift(date, shift) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const label = shift === 'night' ? 'Night shift' : 'Day shift';
  if (!match) return label;
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return `${label} · ${day.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

// The app writes the placeholder "None" for unknown patient fields.
const present = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text !== '' && text.toLowerCase() !== 'none' ? text : '';
};

// "MRN 00123", or '' for sheets without one (made before MRNs, or by hand).
// The MRN identifies the patient but isn't shown prominently.
export function patientMrn(p = {}) {
  const mrn = present(p.mrn);
  return mrn && `MRN ${mrn}`;
}

// "F, 45Y, Room 12, Admitted 10/1", the phone app's one-line patient summary.
// Missing fields are left out.
export function patientSummary(p = {}) {
  const age = present(p.age);
  return [
    present(p.gender),
    age && (/^\d+$/.test(age) ? `${age}Y` : age),
    present(p.room) && `Room ${present(p.room)}`,
    present(p.admitted) && `Admitted ${present(p.admitted)}`,
  ].filter(Boolean).join(', ');
}

// The summary plus code status and doctor, as on the phone's report sheet:
// "F, 45Y, Room 12, Admitted 10/1, Code: Full, Dr. Smith".
export function patientIdentifiers(p = {}) {
  const doctor = present(p.doctor);
  return [
    patientSummary(p),
    present(p.code) && `Code: ${present(p.code)}`,
    doctor && (/^dr\.?\s/i.test(doctor) ? doctor : `Dr. ${doctor}`),
  ].filter(Boolean).join(', ');
}

// "Oct 5", or "Oct 5, 2025" in another year.
export function formatShortDate(iso, now = new Date()) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString([], {
    month: 'short', day: 'numeric', ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' }),
  });
}
